import {
  ConfigProvider,
  Input as AntInput,
  InputNumber as AntInputNumber,
  InputNumberProps,
} from 'antd';
import styled, { css } from 'styled-components';
import React, { ReactNode, useState } from 'react';
import { InputProps } from 'antd/lib/input/Input';
import { TextAreaProps as TextAreaComponentProps } from 'antd/lib/input';
import { Flex } from '@ui-library/flex';
import { Subtext, LargeText } from '@ui-library/typography';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Tooltip } from '@ui-library/tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import theme from '@css/theme';
import { RedAsterisk } from '@components/common/redAsterisk';

import { nextVariant } from '../styles';
import { nextInputTheme } from '../next/tokens';

export { Search } from './search';
export { SearchV2 } from './search';

type Common = {
  isError?: boolean;
  errorMessage?: string;
  errorMessageClass?: string;
  customClass?: string;
  label?: string | ReactNode;
  hideIcon?: boolean;
  required?: boolean;
  block?: boolean;
  variant?: 'next';
};

export type InputComponentProps = InputProps & Common;

export const InputField = ({
  label,
  isError,
  errorMessageClass,
  customClass,
  hideIcon,
  errorMessage,
  required,
  block,
  variant,
  size = 'large',
  ...rest
}: InputComponentProps) => {
  const element = (
    <StyledInput
      $variant={variant}
      data-testid={typeof label === 'string' ? label : undefined}
      {...rest}
      size={size}
      status={isError ? 'error' : undefined}
    />
  );

  return (
    <Flex flexGrow={block} className={customClass} gap="0.25rem" flexDirection="column">
      {label && (
        <LargeText>
          {label}
          {required && <RedAsterisk />}
        </LargeText>
      )}

      {variant === 'next' ? (
        <ConfigProvider theme={nextInputTheme}>{element}</ConfigProvider>
      ) : (
        element
      )}

      {isError && (
        <Flex alignItemsCenter className={errorMessageClass}>
          {!hideIcon && (
            <FontAwesomeIcon
              icon={regular('circle-exclamation')}
              className="m-r-xs"
              color={theme.token.colorError}
            />
          )}
          <Subtext error={isError}>{errorMessage}</Subtext>
        </Flex>
      )}
    </Flex>
  );
};

export type TextAreaProps = TextAreaComponentProps & Common;

export const TextArea = ({
  label,
  isError,
  errorMessageClass,
  hideIcon,
  required,
  errorMessage,
  variant,
  size = 'large',
  ...rest
}: TextAreaProps) => {
  const element = (
    <StyledTextArea
      $variant={variant}
      data-testid={typeof label === 'string' ? label : undefined}
      {...rest}
      size={size}
      status={isError ? 'error' : undefined}
    />
  );

  return (
    <Flex flexDirection="column" gap="0.25rem">
      {label && (
        <LargeText>
          {label}
          {required && <RedAsterisk />}
        </LargeText>
      )}

      {variant === 'next' ? (
        <ConfigProvider theme={nextInputTheme}>{element}</ConfigProvider>
      ) : (
        element
      )}

      {isError && (
        <Flex alignItemsCenter className={errorMessageClass}>
          {!hideIcon && (
            <FontAwesomeIcon
              icon={regular('circle-exclamation')}
              className="m-r-xs"
              color={theme.token.colorError}
            />
          )}
          <Subtext error={isError}>{errorMessage}</Subtext>
        </Flex>
      )}
    </Flex>
  );
};

export type PasswordInputProps = InputComponentProps & {
  isError?: boolean;
  errorMessage?: string;
  testId?: string;
  variant?: 'next';
};

export const PasswordInput = ({
  isError,
  errorMessage = '',
  testId,
  variant,
  size = 'large',
  ...rest
}: PasswordInputProps) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <InputField
      {...rest}
      size={size}
      variant={variant}
      data-testid={testId}
      autoComplete="new-password"
      type={showPassword ? 'text' : 'password'}
      isError={isError || errorMessage.length > 0}
      errorMessage={errorMessage}
      suffix={
        <Tooltip title={showPassword ? 'Hide' : 'Show'} trigger="hover" placement="top">
          <FontAwesomeIcon
            data-testid="eye"
            onClick={() => setShowPassword((x) => !x)}
            icon={showPassword ? regular('eye-slash') : regular('eye')}
          />
        </Tooltip>
      }
    />
  );
};

// ---------------------------------------------------------------------------
// "next" variant components — ConfigProvider handles most styling via tokens.
// Minimal CSS below is only for things tokens can't express.
// ---------------------------------------------------------------------------

type InputNumberVariant = 'next';

interface InputNumberFieldProps extends InputNumberProps {
  variant?: InputNumberVariant;
}

/**
 * Minimal CSS for InputNumber next variant.
 * ConfigProvider handles:
 * - height
 * - border-radius
 * - border-color
 * - background-color
 *
 * hover border, focus border + shadow, error shadow.
 */
const StyledInputNumber = styled(AntInputNumber)<{ $variant?: InputNumberVariant }>`
  ${({ $variant }) =>
    $variant === 'next' &&
    css`
      /* Hover shadow ring — Ant only changes border on hover, not box-shadow */
      &:hover:not(:focus):not(:focus-within):not(.ant-input-number-disabled):not(
          .ant-input-number-status-error
        ) {
        ${nextVariant.hover}
      }

      /* Focus + hover: keep focus styles */
      &:hover:focus,
      &:hover:focus-within {
        ${nextVariant.focus}
      }

      /* Error + hover: add shadow, pin border */
      &.ant-input-number-status-error:hover:not(:focus):not(:focus-within):not(
          .ant-input-number-disabled
        ) {
        ${nextVariant.errorInteraction}
      }

      /* Error + focus + hover: keep error focus styles */
      &.ant-input-number-status-error:hover:focus,
      &.ant-input-number-status-error:hover:focus-within {
        ${nextVariant.errorInteraction}
      }

      /* Disabled: border matches bg */
      &.ant-input-number-disabled {
        ${nextVariant.disabled}
      }

      &.ant-input-number-disabled .ant-input-number-input:not(:placeholder-shown) {
        font-weight: ${({ theme }) => theme.token.next.fontWeight500} !important;
      }

      /* Handler wrap (not tokenizable) */
      .ant-input-number-handler-wrap {
        border-radius: 0 ${({ theme }) => theme.token.next.borderRadius}
          ${({ theme }) => theme.token.next.borderRadius} 0;
        border-color: ${({ theme }) => theme.token.next.colorBorder};
        background-color: ${({ theme }) => theme.token.next.colorBgDisabled};
      }

      .ant-input-number-handler {
        border-color: ${({ theme }) => theme.token.next.colorBorder} !important;
      }

      .ant-input-number-handler:hover {
        background-color: transparent;
        color: ${({ theme }) => theme.token.next.colorBorderFocus};
      }

      &.ant-input-number-disabled .ant-input-number-handler-wrap {
        border-color: ${({ theme }) => theme.token.next.colorBgDisabled};
      }

      &.ant-input-number-disabled .ant-input-number-handler {
        border-color: ${({ theme }) => theme.token.next.colorBgDisabled} !important;
      }
    `}
`;

const InputNumberField: React.FC<InputNumberFieldProps> = ({
  variant,
  size = variant === 'next' ? 'middle' : 'large',
  ...rest
}) => {
  const element = <StyledInputNumber $variant={variant} size={size} {...rest} />;

  if (variant === 'next') {
    return <ConfigProvider theme={nextInputTheme}>{element}</ConfigProvider>;
  }
  return element;
};

type InputVariant = 'next';

interface InputWithVariantProps extends InputProps {
  variant?: InputVariant;
}

/**
 * Minimal CSS for Input/TextArea next variant.
 * ConfigProvider handles: height, border-radius, border-color, bg,
 * hover border, focus border + shadow, error border + shadow, disabled text.
 */
const nextInputMinimalStyles = css`
  /* Hover shadow ring — Ant only changes border on hover */
  &:hover:not(:focus):not(:focus-within):not(:disabled):not(.ant-input-status-error) {
    ${nextVariant.hover}
  }

  /* Focus + hover: keep focus styles (Ant hover CSS can override focus border) */
  &:hover:focus,
  &:hover:focus-within {
    ${nextVariant.focus}
  }

  /* Error + hover: add shadow, pin border (Ant may derive a different hover shade) */
  &.ant-input-status-error:hover:not(:focus):not(:focus-within):not(:disabled) {
    ${nextVariant.errorInteraction}
  }

  /* Error + focus + hover: keep error focus styles */
  &.ant-input-status-error:hover:focus,
  &.ant-input-status-error:hover:focus-within {
    ${nextVariant.errorInteraction}
  }

  /* Disabled: border matches bg */
  &:disabled,
  &[disabled] {
    ${nextVariant.disabled}
  }

  /* Disabled with value: font-weight 500; placeholder stays at default 400 */
  &:disabled:not(:placeholder-shown),
  &[disabled]:not(:placeholder-shown) {
    font-weight: ${({ theme }) => theme.token.next.fontWeight500} !important;
  }
`;

const StyledInput = styled(AntInput)<{ $variant?: InputVariant }>`
  ${({ $variant }) =>
    $variant === 'next' &&
    css`
      &.ant-input {
        ${nextInputMinimalStyles}
      }
    `}
`;

const StyledTextArea = styled(AntInput.TextArea)<{ $variant?: InputVariant }>`
  ${({ $variant }) =>
    $variant === 'next' &&
    css`
      &.ant-input {
        ${nextInputMinimalStyles}
      }
    `}
`;

/**
 * Minimal CSS for affix-wrapper components (Password).
 * ConfigProvider tokens cascade to the affix wrapper.
 */
const nextAffixMinimalStyles = css`
  &.ant-input-affix-wrapper:hover:not(.ant-input-affix-wrapper-focused):not(
      .ant-input-affix-wrapper-disabled
    ):not(.ant-input-affix-wrapper-status-error) {
    ${nextVariant.hover}
  }

  /* Focus + hover: keep focus styles */
  &.ant-input-affix-wrapper.ant-input-affix-wrapper-focused:hover {
    ${nextVariant.focus}
  }

  /* Error + hover: add shadow, pin border */
  &.ant-input-affix-wrapper.ant-input-affix-wrapper-status-error:hover:not(
      .ant-input-affix-wrapper-focused
    ):not(.ant-input-affix-wrapper-disabled) {
    ${nextVariant.errorInteraction}
  }

  /* Error + focus + hover: keep error focus styles */
  &.ant-input-affix-wrapper.ant-input-affix-wrapper-status-error.ant-input-affix-wrapper-focused:hover {
    ${nextVariant.errorInteraction}
  }

  &.ant-input-affix-wrapper-disabled {
    ${nextVariant.disabled}
  }

  &.ant-input-affix-wrapper-disabled input:not(:placeholder-shown) {
    font-weight: ${({ theme }) => theme.token.next.fontWeight500} !important;
  }
`;

const StyledPassword = styled(AntInput.Password)<{ $variant?: InputVariant }>`
  ${({ $variant }) => $variant === 'next' && nextAffixMinimalStyles}
`;

const InputBase = React.forwardRef<React.ComponentRef<typeof AntInput>, InputWithVariantProps>(
  ({ variant, ...rest }, ref) => {
    const element = <StyledInput $variant={variant} ref={ref} {...rest} />;

    if (variant === 'next') {
      return <ConfigProvider theme={nextInputTheme}>{element}</ConfigProvider>;
    }

    return element;
  },
);

InputBase.displayName = 'Input';

const InputTextArea: React.FC<
  InputWithVariantProps & React.ComponentProps<typeof AntInput.TextArea>
> = ({ variant, ...rest }) => {
  const element = <StyledTextArea $variant={variant} {...rest} />;

  if (variant === 'next') {
    return <ConfigProvider theme={nextInputTheme}>{element}</ConfigProvider>;
  }
  return element;
};

const InputPassword: React.FC<
  InputWithVariantProps & React.ComponentProps<typeof AntInput.Password>
> = ({ variant, ...rest }) => {
  const element = <StyledPassword $variant={variant} {...rest} />;

  if (variant === 'next') {
    return <ConfigProvider theme={nextInputTheme}>{element}</ConfigProvider>;
  }
  return element;
};

const InputSearch: React.FC<React.ComponentProps<typeof AntInput.Search>> = (props) => (
  <AntInput.Search {...props} />
);

export const Input = Object.assign(InputBase, {
  TextArea: InputTextArea,
  Password: InputPassword,
  Search: InputSearch,
  Number: InputNumberField,
  Group: AntInput.Group,
});

export type { InputProps } from 'antd';
export { InputNumber } from 'antd';
